import React, {useState, useEffect, useMemo} from 'react';
import axios from 'axios';
import './PermitTypeDisplay.css';
import {Button, Dialog, DialogActions, DialogContent, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem} from '@mui/material';
import {FaEdit, FaMapMarkedAlt, FaUserSecret} from "react-icons/fa";
import {useFeatureExists} from "../../pages/InteractiveMap/useFeatureExists";

const PermitTypeDisplay = ({
                               id,
                               caseId = null,
                               caseData = null,
                               caseIsSectert = false,
                               canEdit = false,
                               permitTypeList = [],
    onEdit
                           }) => {
    const [fullPath, setFullPath] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const featureExists = useFeatureExists(caseData);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [selectedPermitTypeId, setSelectedPermitTypeId] = useState(id);
    const [treeData, setTreeData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const closeEditDialog = () => {
        setIsEditModalVisible(false);
        setSelectedPermitTypeId(id);
    };

    useEffect(() => {
        setSelectedPermitTypeId(id);
    }, [id]);

    const flattenPermitTypes = (items) => {
        const result = [];
        const stack = [...items];
        while (stack.length) {
            const current = stack.shift();
            result.push({ id: current.id, title: current.title });
            if (current.childrenPermitTypes && current.childrenPermitTypes.length) {
                stack.unshift(...current.childrenPermitTypes);
            }
        }
        return result;
    };

    const flatOptions = useMemo(() => flattenPermitTypes(permitTypeList), [permitTypeList]);

    const findItemAndParents = (items, searchId, parents = []) => {
        for (const item of items) {
            if (item.id === searchId) {
                return { path: [...parents, { title: item.title }] };
            }
            if (item.childrenPermitTypes && item.childrenPermitTypes.length) {
                const result = findItemAndParents(item.childrenPermitTypes, searchId, [...parents, { title: item.title }]);
                if (result.path) return { path: result.path };
            }
        }
        return { path: null };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/GetPermitTypeTree`);
                const { data } = response.data;
                setTreeData(data);
                const result = findItemAndParents(data, id);
                if (id === 39) {
                    setFullPath("სამრეწველო დანიშნულების ფეთქებადი მასალების გამოყენების ნებართვა");
                } else if (result.path) {
                    setFullPath(result.path.map(item => item.title).join(' / '));
                } else {
                    setFullPath('');
                }
            } catch {
                setFullPath('');
            } finally {
                setIsLoading(false);
            }
        };
        void fetchData();
    }, [id]);

    const updatePathFromId = (searchId) => {
        const result = findItemAndParents(treeData.length ? treeData : permitTypeList, Number(searchId));
        if (Number(searchId) === 39) {
            setFullPath("სამრეწველო დანიშნულების ფეთქებადი მასალების გამოყენების ნებართვა");
        } else if (result.path) {
            setFullPath(result.path.map(item => item.title).join(' / '));
        }
    };

    const handleSave = async () => {
        if (!caseId || !selectedPermitTypeId) return;
        try {
            setIsSaving(true);
            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/UpdateCasePermitTypeId`, {
                caseId: Number(caseId),
                permitTypeId: Number(selectedPermitTypeId)
            });
            updatePathFromId(selectedPermitTypeId);
            setIsEditModalVisible(false);
        } catch {
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            {isLoading ? (
                <div className="permit-type-display-loader"></div>
            ) : (
                <div className="permit-type-display">
                    {caseIsSectert && (
                        <Tooltip title="განაცხადი გასაიდუმლებულია ">
                            <div className="secret-case-logo">
                                <FaUserSecret className={"secret-case-logo"} />
                            </div>
                        </Tooltip>
                    )}
                    <Tooltip title={fullPath}>
                        {id === 39 ? (
                            <span>{"სამრეწველო დანიშნულების ფეთქებადი მასალების გამოყენების ნებართვა"}</span>
                        ) : (
                            <span style={{ width: "80%" }}>
                {fullPath}
                                {canEdit && (
                                    <Tooltip title="რედაქტირება">
                                        <IconButton size="small" color="primary" onClick={() => setIsEditModalVisible(true)}>
                                            <FaEdit />
                                        </IconButton>
                                    </Tooltip>
                                )}
              </span>
                        )}
                    </Tooltip>

                    {caseId && featureExists && (
                        <Tooltip title={"ობიექტის ნახვა რუკაზე"}>
                            <div
                                className="info-map-logo"
                                onClick={() => window.open(`/interactivemap?id=${caseId}`, '_blank')}
                            >
                                <FaMapMarkedAlt />
                            </div>
                        </Tooltip>
                    )}
                </div>
            )}

            <Dialog open={isEditModalVisible} onClose={closeEditDialog} fullWidth maxWidth="sm">
                <DialogContent>
                    <FormControl fullWidth>
                        <InputLabel id="permit-type-select-label">აირჩიეთ ნებართვის ტიპი</InputLabel>
                        <Select
                            labelId="permit-type-select-label"
                            value={selectedPermitTypeId ?? ''}
                            label="აირჩიეთ ნებართვის ტიპი"
                            onChange={(e) => setSelectedPermitTypeId(e.target.value)}
                        >
                            {flatOptions.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditDialog} disabled={isSaving}>გაუქმება</Button>
                    <Button onClick={handleSave} variant="contained" disabled={isSaving || !caseId}>შენახვა</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PermitTypeDisplay;
